// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto Genesis tokens + glass-card table chrome. The queryFn line below is
// intentionally byte-identical to origin/main -- a parallel fix is
// changing listInstances' data layer; this pass only touches presentation.
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { listInstances } from '@/lib/grpcweb/console-api'
import {
  deploymentTypeLabel,
  enrollmentStatusLabel,
  formatRelativeAge,
  isHandshakeStale,
} from '@/lib/formatters'
import { DARK, GOLD, GREY_300, GREY_400, GREY_500, WARNING, WHITE, errorBlockStyle } from '@/lib/genesis'

export function FleetPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['fleet', 'instances'],
    queryFn: () => listInstances({}),
  })

  return (
    <div>
      <h1 className="font-display mb-4 text-xl font-semibold" style={{ color: WHITE }}>Fleet</h1>

      {isLoading && <p className="text-sm" style={{ color: GREY_400 }}>Loading instances…</p>}

      {isError && (
        <div className="px-3 py-2" style={errorBlockStyle}>
          <p className="text-sm" style={{ color: WHITE }}>
            Failed to load instances: {(error as Error)?.message ?? 'unknown error'}
          </p>
        </div>
      )}

      {!isLoading && !isError && data && data.getInstancesList().length === 0 && (
        <p className="text-sm" style={{ color: GREY_400 }}>
          No instances enrolled yet. Instances register through the Path B
          registration-token flow (ADR-118 D7) or arrive pre-linked via
          marketplace provisioning (Path A).
        </p>
      )}

      {!isLoading && !isError && data && data.getInstancesList().length > 0 && (
        <div className="glass-card overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left" style={{ borderBottom: `1px solid ${GREY_500}`, color: GREY_400 }}>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Environment</th>
                <th className="px-3 py-2 font-medium">Enrollment</th>
                <th className="px-3 py-2 font-medium">Deployment</th>
                <th className="px-3 py-2 font-medium">Last handshake</th>
                <th className="px-3 py-2 font-medium">License</th>
              </tr>
            </thead>
            <tbody>
              {data.getInstancesList().map((inst) => {
                const stale = isHandshakeStale(inst.getLastHandshakeAt())
                return (
                  <tr
                    key={inst.getId()}
                    className="cursor-pointer transition-colors hover:bg-white/5"
                    style={{ borderBottom: `1px solid ${DARK}` }}
                  >
                    <td className="px-3 py-2">
                      <Link
                        to="/fleet/$instanceId"
                        params={{ instanceId: inst.getId() }}
                        className="hover:underline"
                        style={{ color: WHITE }}
                      >
                        {inst.getName() || inst.getId()}
                      </Link>
                    </td>
                    <td className="px-3 py-2" style={{ color: GREY_400 }}>
                      {inst.getEnvironment() || '—'}
                    </td>
                    <td className="px-3 py-2" style={{ color: GREY_300 }}>
                      {enrollmentStatusLabel(inst.getEnrollmentStatus())}
                    </td>
                    <td className="px-3 py-2" style={{ color: GREY_400 }}>
                      {deploymentTypeLabel(inst.getDeploymentType())}
                    </td>
                    <td className="px-3 py-2 font-mono" style={{ color: stale ? WARNING : GREY_400, fontWeight: stale ? 500 : 400 }}>
                      {formatRelativeAge(inst.getLastHandshakeAt())}
                      {stale && ' (stale)'}
                    </td>
                    <td className="px-3 py-2">
                      {inst.getLicenseId() ? (
                        <span className="rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: 'rgba(212, 184, 92, 0.12)', color: GOLD }}>
                          assigned
                        </span>
                      ) : (
                        <span className="rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: DARK, color: GREY_500 }}>
                          unassigned
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
